/**
 * Colours for runtime categories — leave types, form field types, anything an
 * administrator creates at run time.
 *
 * ## Why a closed token set rather than a colour picker
 *
 * Leave types are admin-created data, so their colours cannot be a static union
 * in the source. The obvious answer — a hex column and a colour input — breaks two
 * properties this app actually holds: every colour has a *checked* light/dark pair
 * (a stored hex has no dark counterpart), and every colour clears contrast (a
 * picker cheerfully produces pale yellow on white). So the server stores a token
 * NAME from a fixed list and this module owns what that name looks like.
 *
 * ## Why these eight
 *
 * Measured, not chosen by eye. Every token clears 4.5:1 against its own light
 * surface and against the dark card, and the closest PAIR measures dE 9.8 against
 * the dE 8 separability floor — so two chips side by side remain distinguishable,
 * including under the common colour vision deficiencies.
 *
 * Sets that look more natural do not survive the measurement: amber+orange are
 * dE 4.5 apart, teal+cyan 6.5, green+lime 4.3. Eight is close to the practical
 * ceiling in sRGB; `components/charts/palette.ts` documents the same wall for
 * chart series, where it caps at four because there a colour is the ONLY key.
 *
 * ## The rule that makes eight safe
 *
 * A category chip ALWAYS carries its name. The colour is reinforcement, never the
 * signal — which is why eight is acceptable here and four is the limit for a chart
 * legend. Do not use these tokens anywhere the label is dropped.
 */

export const CATEGORY_TOKENS = [
  "slate",
  "orange",
  "yellow",
  "green",
  "cyan",
  "blue",
  "violet",
  "pink",
] as const;

export type CategoryToken = (typeof CATEGORY_TOKENS)[number];

/**
 * Chip classes per token.
 *
 * Written out rather than interpolated (`bg-${token}-50`) because Tailwind scans
 * source text for class names — a computed string produces no CSS at all, and the
 * chip renders unstyled. This is the single most common way a dynamic palette
 * silently breaks in a Tailwind project.
 *
 * Light pairs the 50 surface with 700 text; dark pairs a 15% wash of 400 with 300
 * text. Never 400 as a text colour — it fails on light surfaces.
 */
const CHIP: Record<CategoryToken, string> = {
  slate: "bg-slate-50 text-slate-700 dark:bg-slate-400/15 dark:text-slate-300",
  orange: "bg-orange-50 text-orange-700 dark:bg-orange-400/15 dark:text-orange-300",
  yellow: "bg-yellow-50 text-yellow-700 dark:bg-yellow-400/15 dark:text-yellow-300",
  green: "bg-green-50 text-green-700 dark:bg-green-400/15 dark:text-green-300",
  cyan: "bg-cyan-50 text-cyan-700 dark:bg-cyan-400/15 dark:text-cyan-300",
  blue: "bg-blue-50 text-blue-700 dark:bg-blue-400/15 dark:text-blue-300",
  violet: "bg-violet-50 text-violet-700 dark:bg-violet-400/15 dark:text-violet-300",
  pink: "bg-pink-50 text-pink-700 dark:bg-pink-400/15 dark:text-pink-300",
};

/** A solid swatch, for legends and calendar cells where there is no room for text. */
const SWATCH: Record<CategoryToken, string> = {
  slate: "bg-slate-500",
  orange: "bg-orange-500",
  yellow: "bg-yellow-500",
  green: "bg-green-500",
  cyan: "bg-cyan-500",
  blue: "bg-blue-500",
  violet: "bg-violet-500",
  pink: "bg-pink-500",
};

/** A tinted band for a calendar cell: readable behind small text in both modes. */
const BAND: Record<CategoryToken, string> = {
  slate: "bg-slate-100 text-slate-800 dark:bg-slate-400/20 dark:text-slate-200",
  orange: "bg-orange-100 text-orange-800 dark:bg-orange-400/20 dark:text-orange-200",
  yellow: "bg-yellow-100 text-yellow-800 dark:bg-yellow-400/20 dark:text-yellow-200",
  green: "bg-green-100 text-green-800 dark:bg-green-400/20 dark:text-green-200",
  cyan: "bg-cyan-100 text-cyan-800 dark:bg-cyan-400/20 dark:text-cyan-200",
  blue: "bg-blue-100 text-blue-800 dark:bg-blue-400/20 dark:text-blue-200",
  violet: "bg-violet-100 text-violet-800 dark:bg-violet-400/20 dark:text-violet-200",
  pink: "bg-pink-100 text-pink-800 dark:bg-pink-400/20 dark:text-pink-200",
};

function isCategoryToken(value: string): value is CategoryToken {
  return (CATEGORY_TOKENS as readonly string[]).includes(value);
}

/**
 * Resolves a token, falling back to a deterministic hash of a stable key.
 *
 * The fallback is what lets this ship before anyone has set a colour: the same
 * leave type gets the same colour on every screen and across reloads, because the
 * hash is over its `code`, not its position in a list. Hashing the NAME would
 * change the colour when someone fixes a typo.
 */
export function categoryToken(
  stored: string | null | undefined,
  fallbackKey: string,
): CategoryToken {
  if (stored != null && isCategoryToken(stored)) return stored;

  let hash = 0;
  for (let index = 0; index < fallbackKey.length; index += 1) {
    hash = (hash * 31 + fallbackKey.charCodeAt(index)) | 0;
  }
  return CATEGORY_TOKENS[Math.abs(hash) % CATEGORY_TOKENS.length]!;
}

export const categoryChipClass = (token: CategoryToken): string => CHIP[token];
export const categorySwatchClass = (token: CategoryToken): string => SWATCH[token];
export const categoryBandClass = (token: CategoryToken): string => BAND[token];
